import React, { lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Toaster } from './components/ui/toaster';

import Header from './components/Header';
import PromoBar from './components/PromoBar';
import Hero from './components/Hero';
import Footer from './components/Footer';
import MobileBuyBar from './components/MobileBuyBar';
// Seções abaixo da primeira tela: vêm em arquivos separados, para o topo da página carregar mais rápido.
const CompaniesMarquee = lazy(() => import('./components/CompaniesMarquee'));
const Clients = lazy(() => import('./components/Clients'));
const HowItWorks = lazy(() => import('./components/HowItWorks'));
const Personalization = lazy(() => import('./components/Personalization'));
const LeadsSection = lazy(() => import('./components/LeadsSection'));
const Corporate = lazy(() => import('./components/Corporate'));
const HowFunctions = lazy(() => import('./components/HowFunctions'));
const RoiCalculator = lazy(() => import('./components/RoiCalculator'));
const Products = lazy(() => import('./components/Products'));
const SmartBadge = lazy(() => import('./components/SmartBadge'));
const Testimonials = lazy(() => import('./components/Testimonials'));
const FAQ = lazy(() => import('./components/FAQ'));
const CardEditorSection = lazy(() => import('./components/CardEditorSection'));
const Steps = lazy(() => import('./components/Steps'));
const FinalCTA = lazy(() => import('./components/FinalCTA'));
// Avisa quando todas as seções acima já carregaram (para ligar os efeitos de rolagem nelas).
const SectionsReady = ({ onReady }) => { React.useEffect(onReady, [onReady]); return null; };
import WhatsAppButton from './components/WhatsAppButton';
const Corporativo = lazy(() => import('./pages/Corporativo'));
const Politicas = lazy(() => import('./pages/legal/Politicas'));
const TermosUso = lazy(() => import('./pages/legal/TermosUso'));
const Lgpd = lazy(() => import('./pages/legal/Lgpd'));
import ScrollToHash from './components/ScrollToHash';
import TrackingProvider from './components/TrackingProvider';
import usePageEffects from './hooks/use-page-effects';
import useSeo from './hooks/use-seo';

// Checkout
const Checkout = lazy(() => import('./pages/Checkout'));
const CheckoutStatus = lazy(() => import('./pages/CheckoutStatus'));

// Admin
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const RequireAuth = lazy(() => import('./pages/admin/AdminLayout').then((m) => ({ default: m.RequireAuth })));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const OrdersList = lazy(() => import('./pages/admin/OrdersList'));
const OrderDetail = lazy(() => import('./pages/admin/OrderDetail'));
const NewOrder = lazy(() => import('./pages/admin/NewOrder'));
const Customers = lazy(() => import('./pages/admin/Customers'));
const Labels = lazy(() => import('./pages/admin/Labels'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const AbandonedCarts = lazy(() => import('./pages/admin/AbandonedCarts'));
const DemoLeads = lazy(() => import('./pages/admin/DemoLeads'));
const Tracking = lazy(() => import('./pages/admin/Tracking'));
const Coupons = lazy(() => import('./pages/admin/Coupons'));
const ProductsAdmin = lazy(() => import('./pages/admin/Products'));
const OrderBump = lazy(() => import('./pages/admin/OrderBump'));
const Analytics = lazy(() => import('./pages/admin/Analytics'));
const CampaignLinks = lazy(() => import('./pages/admin/CampaignLinks'));
const SiteVideo = lazy(() => import('./pages/admin/SiteVideo'));const QuotesList = lazy(() => import('./pages/admin/QuotesList'));
const NewQuote = lazy(() => import('./pages/admin/NewQuote'));
const EditQuote = lazy(() => import('./pages/admin/EditQuote'));
const QuoteDetail = lazy(() => import('./pages/admin/QuoteDetail'));

const Home = () => {
  const [ready, setReady] = React.useState(false);
  const onReady = React.useCallback(() => {
    setReady(true);
    document.getElementById('pp-seo')?.remove(); // texto para buscadores: sai só quando o conteúdo real já está na página
  }, []);
  // As seções abaixo do topo só entram depois que a primeira tela foi "adotada" (hidratada): o HTML do servidor e o
  // primeiro desenho do navegador ficam idênticos, e o resto do JavaScript carrega em seguida.
  const [mounted, setMounted] = React.useState(false);
  // (entram na primeira rolagem, toque, tecla ou clique, ou depois de 3 s parado; com #âncora na URL entram já)
  React.useEffect(() => {
    if (mounted) return undefined;
    const go = () => setMounted(true);
    if (window.location.hash) { go(); return undefined; }
    const evs = ['scroll', 'wheel', 'pointerdown', 'touchstart', 'keydown', 'hashchange'];
    evs.forEach((e) => window.addEventListener(e, go, { once: true, passive: true }));
    const timer = setTimeout(go, 3000);
    return () => { clearTimeout(timer); evs.forEach((e) => window.removeEventListener(e, go)); };
  }, [mounted]);
  usePageEffects(ready);
  useSeo({
    title: 'PlusPlus | Cartão de Visita Eletrônico NFC e QR Code',
    description: 'Cartão de visita eletrônico PlusPlus: compartilhe seu contato por NFC, QR Code ou link e capte leads com CRM. Sem app, com plano grátis.',
    path: '/',
  });
  return (
  <>
    <div className="pp-progress" aria-hidden="true" />
    <PromoBar />
    <Header />
    <main>
      <Hero />
      <Suspense fallback={<div style={{ minHeight: '120vh' }} aria-hidden="true" />}>
        {!mounted ? <div style={{ minHeight: '120vh' }} aria-hidden="true" /> : (
          <>
          <CompaniesMarquee />
          <Clients />
          <Steps />
          <Products />
          <HowItWorks />
          <CardEditorSection />
          <Personalization />
          <LeadsSection />
          <Corporate />
          <HowFunctions />
          <RoiCalculator />
          <SmartBadge />
          <Testimonials />
          <FAQ />
          <FinalCTA />
          <SectionsReady onReady={onReady} />
          </>
        )}
      </Suspense>
    </main>
    <Footer />
    <WhatsAppButton />
    <MobileBuyBar />
    <Toaster />
  </>
  );
};

export const AppContent = () => (
  <>
        <ScrollToHash />
        <TrackingProvider />
        <Suspense fallback={<div className="min-h-screen" aria-busy="true" />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/corporativo" element={<Corporativo />} />
          <Route path="/politicas" element={<Politicas />} />
          <Route path="/termos-uso" element={<TermosUso />} />
          <Route path="/lgpd" element={<Lgpd />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/checkout/sucesso" element={<CheckoutStatus variant="sucesso" />} />
          <Route path="/checkout/falha" element={<CheckoutStatus variant="falha" />} />
          <Route path="/checkout/pendente" element={<CheckoutStatus variant="pendente" />} />

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<RequireAuth><AdminLayout /></RequireAuth>}>
            <Route index element={<Dashboard />} />
            <Route path="pedidos" element={<OrdersList />} />
            <Route path="pedidos/novo" element={<NewOrder />} />
            <Route path="pedidos/:id" element={<OrderDetail />} />
            <Route path="carrinhos" element={<AbandonedCarts />} />
            <Route path="demonstracoes" element={<DemoLeads />} />
            <Route path="rastreamento" element={<Tracking />} />
            <Route path="cupons" element={<Coupons />} />
            <Route path="produtos" element={<ProductsAdmin />} />
            <Route path="order-bump" element={<OrderBump />} />
            <Route path="analise" element={<Analytics />} />
            <Route path="links" element={<CampaignLinks />} />
            <Route path="video" element={<SiteVideo />} />            <Route path="orcamentos" element={<QuotesList />} />
            <Route path="orcamentos/novo" element={<NewQuote />} />
            <Route path="orcamentos/:id/editar" element={<EditQuote />} />
            <Route path="orcamentos/:id" element={<QuoteDetail />} />
            <Route path="clientes" element={<Customers />} />
            <Route path="etiquetas" element={<Labels />} />
            <Route path="configuracoes" element={<Settings />} />
          </Route>
        </Routes>
        </Suspense>
  </>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </div>
  );
}

export default App;
